/**
 * Classifies session errors (RPC failures, stream errors) into structured
 * error info for ActionableError rendering.
 *
 * Handles two error shapes:
 * - ConnectError from gRPC/Connect RPCs (SendToAgentExecution, CreateAgentExecution)
 * - Plain Error from HTTP stream failures (conversation stream)
 */

import { Code, ConnectError } from "@connectrpc/connect";
import type { ClassifiedError } from "@/utils/environment-error-classifier";

/**
 * Classifies a session error into a structured error with a user-friendly message.
 * Returns null if the error doesn't match any known pattern — caller should fall
 * back to formatError.
 */
export function classifySessionError(error: unknown): ClassifiedError | null {
    if (error instanceof ConnectError) {
        return classifyConnectError(error);
    }

    if (error instanceof Error) {
        return classifyStreamError(error.message);
    }

    if (typeof error === "string") {
        return classifyStreamError(error);
    }

    return null;
}

function classifyConnectError(error: ConnectError): ClassifiedError | null {
    const msg = error.rawMessage.toLowerCase();

    switch (error.code) {
        case Code.DeadlineExceeded:
            return {
                errorCode: "SESSION_TIMEOUT",
                userMessage: "The request timed out. This could be temporary, please try again.",
                metadata: {},
            };

        case Code.Unavailable:
            return {
                errorCode: "SESSION_UNAVAILABLE",
                userMessage: "The service is temporarily unavailable. Please try again in a moment.",
                metadata: {},
            };

        case Code.ResourceExhausted:
            if (msg.includes("image upload rate limit")) {
                return {
                    errorCode: "SESSION_IMAGE_RATE_LIMITED",
                    userMessage: "You've sent too many images recently. Please wait a moment and try again.",
                    metadata: {},
                };
            }
            return {
                errorCode: "SESSION_RATE_LIMITED",
                userMessage: "Too many requests. Please wait a moment and try again.",
                metadata: {},
            };

        case Code.Unauthenticated:
            return {
                errorCode: "SESSION_UNAUTHENTICATED",
                userMessage: "Your session has expired. Please sign in again.",
                metadata: {},
            };

        case Code.PermissionDenied:
            return {
                errorCode: "SESSION_PERMISSION_DENIED",
                userMessage: "You don't have permission to perform this action.",
                metadata: {},
            };

        case Code.Internal:
        case Code.Unknown:
            return {
                errorCode: "SESSION_INTERNAL",
                userMessage: "Something went wrong. Please try again or contact support if the issue persists.",
                metadata: {},
            };

        case Code.FailedPrecondition:
            if (msg.includes("does not support agent execution")) {
                return {
                    errorCode: "SESSION_RUNNER_NO_AGENT_SUPPORT",
                    userMessage:
                        "This runner does not support Ona sessions. Ask your administrator to update the runner.",
                    metadata: {},
                };
            }
            if (msg.includes("image attachments are not enabled")) {
                return {
                    errorCode: "SESSION_IMAGES_DISABLED",
                    userMessage: "Image attachments are not enabled for your organization.",
                    metadata: {},
                };
            }
            return null;

        default:
            return null;
    }
}

/**
 * Classifies plain Error messages from HTTP stream failures.
 * These come from fetch() responses and stream parsing in use-stream-conversation.ts.
 */
function classifyStreamError(message: string): ClassifiedError | null {
    const lower = message.toLowerCase();

    if (lower.includes("stream closed unexpectedly")) {
        return {
            errorCode: "SESSION_STREAM_CLOSED",
            userMessage: "The conversation stream was interrupted. Reconnecting…",
            metadata: {},
        };
    }

    if (
        lower.includes("failed to fetch") ||
        lower.includes("stream error") ||
        lower.includes("request validation failed")
    ) {
        return {
            errorCode: "SESSION_STREAM_FAILED",
            userMessage: "Unable to connect to the conversation. This is usually temporary. Reconnecting…",
            metadata: {},
        };
    }

    if (lower.includes("networkerror") || lower.includes("load failed")) {
        return {
            errorCode: "SESSION_NETWORK_ERROR",
            userMessage: "Network connection lost. Check your internet connection.",
            metadata: {},
        };
    }

    return null;
}
